import React, { useRef, useState, useEffect, useMemo } from "react";

const LazyImage = ({
  src,
  alt,
  component,
  styleish,
  imgOnly,
  notFullHeight,
  videoRef,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!videoRef) return;
    if (isVisible && videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  }, [isVisible, videoRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(wrapperRef.current);
        }
      },
      { threshold: 0.8 } // Lower threshold for earlier detection
    );

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current);
    }

    return () => {
      if (wrapperRef.current) {
        observer.unobserve(wrapperRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      if (imgOnly) {
        // For images, wait for load event
        if (imgRef.current?.complete) {
          setIsLoaded(true);
        }
      } else {
        // For components, consider loaded immediately
        setIsLoaded(true);
      }
    }
  }, [isVisible, imgOnly]);

  const renderContent = useMemo(() => {
    if (isVisible) {
      if (imgOnly) {
        return (
          <>
            {!isLoaded && (
              <div style={{
                minHeight: "200px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                ...styleish
              }}>
                <div className="lazy-loader" />
              </div>
            )}
            <img
              ref={imgRef}
              src={src}
              alt={alt}
              className={props.className}
              style={{
                ...styleish,
                display: isLoaded ? 'block' : 'none'
              }}
              onLoad={() => setIsLoaded(true)}
            />
          </>
        );
      } else {
        // Non-image components don't need loading state
        return <div ref={imgRef} style={{ ...styleish }}>{component}</div>;
      }
    }

    // Not visible yet
    return (
      <div style={{
        minHeight: "200px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...styleish
      }}>
        <div className="lazy-loader" />
      </div>
    );
  }, [alt, component, imgOnly, isVisible, isLoaded, props.className, src, styleish]);

  return (
    <div ref={wrapperRef} style={{ height: !notFullHeight && "100%" }}>
      {renderContent}
    </div>
  );
};

export default LazyImage;